import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Activity, CalendarDays, Star, Target, Trophy, Users } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import { supabaseV2 } from "../../lib/supabase";
import { currentIntlLocale } from "../../lib/i18n";
import { calculatePeriodSquadScore } from "../../lib/squad-score";
import { romeDate, seasonPeriodLabel, seasonReferenceNow, todayRome, type Season, type SeasonStats } from "../../lib/seasons";
import { useWorkspaceData } from "../../lib/workspace-data";
import { EmptyState, SectionLoading } from "../../components/state";
import { RankMedal } from "../../components/primitives";
import type { SquadScoreSetting } from "../../types";

type RankRow = { playerId: string; name: string; value: number; hint?: string };

/** Report in sola lettura di una stagione: risultati, presenze, giocatori. */
export function SeasonReport({
  teamId,
  season,
  previous,
  onOpenPlayer,
}: {
  teamId: string;
  season: Season;
  previous: Season | null;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("seasons");
  const { players, events, attendance: responses, matches, matchStats, isLoading } = useWorkspaceData(teamId, { seasonId: season.id });
  const settingQuery = useQuery({
    queryKey: ["squad-score-settings", teamId, "active"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("squad_score_settings")
        .select("*")
        .eq("team_id", teamId)
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data || null) as SquadScoreSetting | null;
    },
    staleTime: 60_000,
  });
  const setting = settingQuery.data;
  // Decay dello Squad Score calcolato a fine stagione per le stagioni chiuse.
  const referenceNow = season.status === "closed" ? seasonReferenceNow(season) : undefined;

  const monthly = useMemo(() => {
    const start = season.starts_on.slice(0, 7);
    const endDate = season.ends_on || todayRome();
    const lastMatch = matches.reduce((acc, m) => {
      const month = romeDate(m.starts_at).slice(0, 7);
      return month > acc ? month : acc;
    }, "");
    const end = [endDate.slice(0, 7), lastMatch].sort().pop() || endDate.slice(0, 7);
    const buckets: Array<{ key: string; label: string; wins: number; draws: number; losses: number; total: number }> = [];
    let [y, m] = start.split("-").map(Number);
    for (let guard = 0; guard < 36; guard += 1) {
      const key = `${y}-${String(m).padStart(2, "0")}`;
      if (key > end) break;
      buckets.push({
        key,
        label: new Date(y, m - 1, 1).toLocaleDateString(currentIntlLocale(), { month: "short" }),
        wins: 0,
        draws: 0,
        losses: 0,
        total: 0,
      });
      m += 1;
      if (m > 12) {
        m = 1;
        y += 1;
      }
    }
    const byKey = new Map(buckets.map((b) => [b.key, b]));
    for (const match of matches) {
      if (!match.starts_at || match.our_score == null || match.opponent_score == null) continue;
      const bucket = byKey.get(romeDate(match.starts_at).slice(0, 7));
      if (!bucket) continue;
      if (match.our_score > match.opponent_score) bucket.wins += 1;
      else if (match.our_score < match.opponent_score) bucket.losses += 1;
      else bucket.draws += 1;
      bucket.total += 1;
    }
    return buckets;
  }, [matches, season.starts_on, season.ends_on]);

  const competitions = useMemo(() => {
    const map = new Map<string, { name: string; played: number; wins: number; draws: number; losses: number; gf: number; ga: number }>();
    for (const match of matches) {
      if (match.our_score == null || match.opponent_score == null) continue;
      const name = match.competition_name?.trim() || t("report.noCompetition");
      const row = map.get(name) || { name, played: 0, wins: 0, draws: 0, losses: 0, gf: 0, ga: 0 };
      row.played += 1;
      row.gf += match.our_score;
      row.ga += match.opponent_score;
      if (match.our_score > match.opponent_score) row.wins += 1;
      else if (match.our_score < match.opponent_score) row.losses += 1;
      else row.draws += 1;
      map.set(name, row);
    }
    return [...map.values()].sort((a, b) => b.played - a.played);
  }, [matches, t]);

  const roster = players.filter((p) => !p.is_guest);
  const nameOf = (id: string) => players.find((p) => p.id === id)?.display_name || "-";

  const rankings = useMemo(() => {
    const goals = new Map<string, number>();
    const assists = new Map<string, number>();
    const starts = new Map<string, number>();
    for (const stat of matchStats) {
      goals.set(stat.player_id, (goals.get(stat.player_id) || 0) + (stat.goals || 0));
      assists.set(stat.player_id, (assists.get(stat.player_id) || 0) + (stat.assists || 0));
      if (stat.started) starts.set(stat.player_id, (starts.get(stat.player_id) || 0) + 1);
    }
    const top = (map: Map<string, number>): RankRow[] =>
      [...map.entries()]
        .filter(([, value]) => value > 0)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([playerId, value]) => ({ playerId, name: nameOf(playerId), value }));

    const presence = roster
      .map((player) => {
        const rows = responses.filter((r) => r.player_id === player.id && r.status !== "no_response");
        const attended = rows.filter((r) => r.status === "present" || r.status === "late").length;
        return { player, attended, total: rows.length };
      })
      .filter((row) => row.total > 0);
    const mostPresent: RankRow[] = [...presence]
      .sort((a, b) => b.attended - a.attended)
      .slice(0, 5)
      .map((row) => ({
        playerId: row.player.id,
        name: row.player.display_name,
        value: row.attended,
        hint: `${Math.round((row.attended / row.total) * 100)}%`,
      }));
    const leastPresent: RankRow[] = [...presence]
      .sort((a, b) => a.attended / a.total - b.attended / b.total)
      .slice(0, 3)
      .map((row) => ({
        playerId: row.player.id,
        name: row.player.display_name,
        value: Math.round((row.attended / row.total) * 100),
        hint: t("report.presenceOf", { attended: row.attended, total: row.total }),
      }));

    const sessionsCount = events.length;
    const squadScore: RankRow[] = setting
      ? roster
          .map((player) => {
            const { score, eventsCount } = calculatePeriodSquadScore(
              responses.filter((r) => r.player_id === player.id),
              matchStats.filter((s) => s.player_id === player.id),
              setting,
              { fromMs: null, toMs: null },
              sessionsCount,
              referenceNow,
            );
            return { player, score, eventsCount };
          })
          .filter((row) => row.eventsCount > 0)
          .sort((a, b) => b.score - a.score)
          .slice(0, 5)
          .map((row) => ({ playerId: row.player.id, name: row.player.display_name, value: Number(row.score.toFixed(1)) }))
      : [];

    return { goals: top(goals), assists: top(assists), starts: top(starts), mostPresent, leastPresent, squadScore };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchStats, responses, players, events.length, setting, referenceNow]);

  if (isLoading) return <SectionLoading />;

  const s = season.stats;
  const p = previous?.stats || null;
  const presencePct = (stats: SeasonStats | null) =>
    stats && stats.responses > 0 ? Math.round(((stats.present + stats.late) / stats.responses) * 100) : null;
  const winPct = (stats: SeasonStats | null) => (stats && stats.played > 0 ? Math.round((stats.wins / stats.played) * 100) : null);
  const delta = (current: number | null, before: number | null, suffix = "") => {
    if (current == null || before == null || !previous) return null;
    const d = current - before;
    return { label: `${d > 0 ? "+" : ""}${d}${suffix}`, positive: d >= 0 };
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h3 className="text-[22px] font-bold leading-tight">{t("report.title", { name: season.name })}</h3>
          <p className="text-xs text-muted-foreground">{seasonPeriodLabel(season)}</p>
        </div>
        {previous && <p className="text-xs text-muted-foreground">{t("report.comparedTo", { name: previous.name })}</p>}
      </div>

      <div className="rounded-[20px] bg-card p-5 grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-5">
        <Kpi
          label={t("report.kpi.record")}
          value={`${s.wins} · ${s.draws} · ${s.losses}`}
          hint={winPct(s) != null ? t("report.kpi.winRate", { pct: winPct(s) }) : t("report.kpi.recordHint")}
          delta={delta(winPct(s), winPct(p), t("report.kpi.pointsSuffix"))}
        />
        <Kpi
          label={t("report.kpi.goals")}
          value={`${s.goals_for} / ${s.goals_against}`}
          hint={t("report.kpi.goalDiff", { value: `${s.goals_for - s.goals_against >= 0 ? "+" : ""}${s.goals_for - s.goals_against}` })}
          delta={delta(s.goals_for - s.goals_against, p ? p.goals_for - p.goals_against : null)}
        />
        <Kpi
          label={t("report.kpi.presence")}
          value={presencePct(s) != null ? `${presencePct(s)}%` : "-"}
          hint={t("report.kpi.responses", { count: s.responses })}
          delta={delta(presencePct(s), presencePct(p), t("report.kpi.pointsSuffix"))}
        />
        <Kpi
          label={t("report.kpi.trainings")}
          value={s.past_trainings}
          hint={t("report.kpi.matchDays", { count: s.match_days })}
          delta={delta(s.past_trainings, p ? p.past_trainings : null)}
        />
      </div>

      {s.excluded_matches > 0 && (
        <p className="text-xs text-muted-foreground">{t("report.excludedNote", { count: s.excluded_matches })}</p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              {t("report.results.title")}
            </CardTitle>
            <CardDescription>{t("report.results.description")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {s.played === 0 ? (
              <EmptyState text={t("report.results.empty")} />
            ) : (
              <>
                <MonthlyChart data={monthly} />
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-muted-foreground">
                        <th className="text-left eb-data text-[13px] py-1.5 pr-2">{t("report.results.competition")}</th>
                        <th className="text-right eb-data text-[13px] px-1">{t("report.results.played")}</th>
                        <th className="text-right eb-data text-[13px] px-1">{t("report.results.wdl")}</th>
                        <th className="text-right eb-data text-[13px] pl-1">{t("report.results.goals")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competitions.map((row) => (
                        <tr key={row.name} className="border-t">
                          <td className="py-1.5 pr-2 font-medium truncate max-w-[180px]">{row.name}</td>
                          <td className="text-right tabular-nums px-1">{row.played}</td>
                          <td className="text-right tabular-nums px-1">{row.wins}·{row.draws}·{row.losses}</td>
                          <td className="text-right tabular-nums pl-1">{row.gf}-{row.ga}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              {t("report.presence.title")}
            </CardTitle>
            <CardDescription>
              {t("report.presence.description", { trainings: s.past_trainings, matchDays: s.match_days })}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <RankList title={t("report.presence.most")} rows={rankings.mostPresent} onOpenPlayer={onOpenPlayer} />
            <RankList title={t("report.presence.least")} rows={rankings.leastPresent} valueSuffix="%" onOpenPlayer={onOpenPlayer} />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            {t("report.players.title")}
          </CardTitle>
          <CardDescription>{t("report.players.description")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <RankList title={t("report.players.goals")} rows={rankings.goals} onOpenPlayer={onOpenPlayer} />
          <RankList title={t("report.players.assists")} rows={rankings.assists} onOpenPlayer={onOpenPlayer} />
          <RankList title={t("report.players.starts")} rows={rankings.starts} onOpenPlayer={onOpenPlayer} />
          <RankList
            title={t("report.players.squadScore")}
            rows={rankings.squadScore}
            empty={setting ? undefined : t("report.players.noPreset")}
            onOpenPlayer={onOpenPlayer}
          />
        </CardContent>
      </Card>
    </section>
  );
}

function Kpi({
  label,
  value,
  hint,
  delta,
}: {
  label: string;
  value: string | number;
  hint?: string;
  delta: { label: string; positive: boolean } | null;
}) {
  return (
    <div className="min-w-0">
      <p className="eb-num text-[34px] pr-1 whitespace-nowrap overflow-hidden">{value}</p>
      <p className="text-[13px] leading-tight text-muted-foreground">{label}</p>
      <p className="mt-0.5 flex flex-wrap gap-x-2 text-xs leading-tight text-[hsl(var(--faint))]">
        {hint && <span>{hint}</span>}
        {delta && <span className={cn("tabular-nums", delta.positive ? "text-[hsl(var(--ok))]" : "text-[hsl(var(--danger))]")}>{delta.label}</span>}
      </p>
    </div>
  );
}

function RankList({
  title,
  rows,
  valueSuffix = "",
  empty,
  onOpenPlayer,
}: {
  title: string;
  rows: RankRow[];
  valueSuffix?: string;
  empty?: string;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("seasons");
  return (
    <div className="min-w-0">
      <p className="eb-eyebrow mb-1.5">{title}</p>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">{empty || t("report.noData")}</p>
      ) : (
        <ol className="space-y-0.5">
          {rows.map((row, idx) => (
            <li key={row.playerId}>
              <button
                type="button"
                onClick={() => onOpenPlayer(row.playerId)}
                className="w-full flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-raised text-left"
              >
                <RankMedal rank={idx + 1} />
                <span className="flex-1 min-w-0 truncate text-sm">{row.name}</span>
                {row.hint && <span className="text-xs text-muted-foreground tabular-nums">{row.hint}</span>}
                <span className="eb-num text-[20px] pr-1">
                  {row.value}
                  {valueSuffix}
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function MonthlyChart({
  data,
}: {
  data: Array<{ key: string; label: string; wins: number; draws: number; losses: number; total: number }>;
}) {
  const max = Math.max(1, ...data.map((d) => d.total));
  return (
    <div className="overflow-x-auto">
      <div
        className="grid gap-1.5 h-32 items-end"
        style={{ gridTemplateColumns: `repeat(${Math.max(data.length, 1)}, minmax(1.75rem, 1fr))` }}
      >
        {data.map((bucket) => {
          const heightPct = (bucket.total / max) * 100;
          return (
            <div key={bucket.key} className="flex flex-col items-center justify-end gap-1 h-full" title={`${bucket.wins}·${bucket.draws}·${bucket.losses}`}>
              <span className="text-xs text-muted-foreground tabular-nums">{bucket.total || ""}</span>
              <div
                className="w-full max-w-9 rounded-md overflow-hidden flex flex-col gap-0.5"
                style={{ height: `${heightPct}%`, minHeight: bucket.total > 0 ? 6 : 0 }}
              >
                {bucket.losses > 0 && <div className="bg-destructive" style={{ flexGrow: bucket.losses }} />}
                {bucket.draws > 0 && <div className="bg-warning" style={{ flexGrow: bucket.draws }} />}
                {bucket.wins > 0 && <div className="bg-success" style={{ flexGrow: bucket.wins }} />}
              </div>
              <span className="text-xs text-muted-foreground capitalize">{bucket.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
